/**
 * CompliancePosturePage — admin dashboard for the Compliance & Freshness
 * Watchdog (Task #34).
 *
 * Read-only view of every check the watchdog has run, with severity-coded
 * styling, filter chips by category and severity, per-check detail panel
 * with full history. Designed to live alongside the SecurityEnginePage
 * — together they form the platform's complete drift + posture surface.
 *
 * Talks to the IPC channels in routes/compliance_watchdog.js:
 *   compliance-watchdog:list-latest
 *   compliance-watchdog:get-history
 *   compliance-watchdog:summary
 *
 * Permission: compliance:read (sysadmin + admin).
 * See docs/compliance_watchdog_design_2026-06-07.md.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Activity, AlertCircle, AlertTriangle, CheckCircle2, Info, Loader2,
  ShieldAlert, Wrench, RefreshCcw,
} from "lucide-react";

const SEVERITY_STYLE = {
  info:     { bg: "#e8f0fb", border: "#c9d6e8", color: "#1f6feb", label: "INFO",     I: CheckCircle2 },
  warn:     { bg: "#fef6e0", border: "#e0b973", color: "#7c5210", label: "WARN",     I: AlertTriangle },
  error:    { bg: "#fdecdc", border: "#e8a070", color: "#7c3a10", label: "ERROR",    I: Wrench },
  critical: { bg: "#fde8e8", border: "#f5a5a5", color: "#b00020", label: "CRITICAL", I: AlertCircle },
};

const CATEGORY_LABEL = {
  infra:        "Infrastructure",
  supply_chain: "Supply chain",
  hipaa:        "HIPAA controls",
  gdpr:         "GDPR controls",
  schema:       "Database schema",
  secret:       "Secrets / keys",
  liveness:     "Cron liveness",
  model:        "AI models",
};

export default function CompliancePosturePage({ user }) {
  const [findings, setFindings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);   // {finding, history}
  const [filters, setFilters] = useState({ severities: [], categories: [] });

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const api = window.zignagenixAPI;
      const [list, sum] = await Promise.all([
        api.invoke("compliance-watchdog:list-latest", filters).catch(() => []),
        api.invoke("compliance-watchdog:summary").catch(() => null),
      ]);
      setFindings(Array.isArray(list) ? list : []);
      setSummary(sum);
    } catch (e) {
      setError((e && e.message) || "Failed to load watchdog state");
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => { loadAll(); }, [loadAll]);

  async function openCheck(check_id) {
    setSelected({ loading: true, check_id });
    try {
      const rows = await window.zignagenixAPI.invoke(
        "compliance-watchdog:get-history", { check_id, limit: 30 }
      );
      const latest = findings.find((f) => f.check_id === check_id);
      setSelected({ finding: latest, history: Array.isArray(rows) ? rows : [] });
    } catch (e) {
      setSelected({ error: (e && e.message) || "Failed to load history" });
    }
  }

  const counts = useMemo(() => {
    const c = { critical: 0, error: 0, warn: 0, info: 0, total: findings.length };
    for (const f of findings) c[f.severity] = (c[f.severity] || 0) + 1;
    return c;
  }, [findings]);

  function toggleFilter(kind, value) {
    setFilters((prev) => {
      const arr = prev[kind] || [];
      const next = arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
      return { ...prev, [kind]: next };
    });
  }

  return (
    <div className="compliance-posture-page" style={{ padding: "16px 24px", maxWidth: 1400, margin: "0 auto" }}>

      {/* ── Header ─────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 22, marginBottom: 4 }}>Compliance Posture (Watchdog)</h1>
          <p style={{ color: "#5a6478", fontSize: 13, margin: 0 }}>
            Drift detection across infrastructure, supply chain, schema, secrets, and AI models.{" "}
            <span style={{ color: "#9aa4b2" }}>HIPAA technical safeguards · GDPR Art. 32 · Mexico LFPDPPP Art. 19 / 39 evidence trail.</span>
          </p>
        </div>
        <button onClick={loadAll} disabled={loading} style={btnGhost}>
          <RefreshCcw size={13} /> Refresh
        </button>
      </div>

      {error && (
        <div style={alertStyle("error")}><AlertCircle size={14} /> {error}</div>
      )}

      {/* ── Summary cards ───────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        <SummaryCard label="Critical" value={counts.critical} sev="critical" />
        <SummaryCard label="Error (run failures)" value={counts.error} sev="error" />
        <SummaryCard label="Warnings" value={counts.warn} sev="warn" />
        <SummaryCard label="Total checks" value={counts.total} sev="info" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 18 }}>

        {/* ── Findings list (left) ──────────────────────────────── */}
        <div>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 8, gap: 6, flexWrap: "wrap" }}>
            <h2 style={{ fontSize: 15, margin: 0, marginRight: 8 }}>Checks ({findings.length})</h2>
            {["critical", "warn", "error", "info"].map((s) => (
              <FilterChip
                key={s}
                label={s}
                active={filters.severities.includes(s)}
                onClick={() => toggleFilter("severities", s)}
              />
            ))}
          </div>

          {loading ? (
            <div style={{ padding: 24, color: "#9aa4b2", textAlign: "center" }}>
              <Loader2 className="spin" /> Loading checks…
            </div>
          ) : findings.length === 0 ? (
            <div style={{ padding: 24, color: "#9aa4b2", textAlign: "center", fontStyle: "italic", border: "1px dashed #c9d6e8", borderRadius: 4 }}>
              No watchdog runs yet. The daily cron at 06:00 UTC will populate this once the service has been up for a full cycle, or run it on-demand via GitHub Actions workflow_dispatch on `.github/workflows/compliance_watchdog.yml`.
            </div>
          ) : (
            <div style={{ border: "1px solid #e1e6ed", borderRadius: 4, maxHeight: 640, overflowY: "auto" }}>
              {findings.map((f) => (
                <CheckRow
                  key={f.id}
                  finding={f}
                  active={selected && selected.finding && selected.finding.id === f.id}
                  onClick={() => openCheck(f.check_id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── Detail panel (right) ─────────────────────────────── */}
        <div>
          {selected ? (
            <CheckDetail data={selected} onClose={() => setSelected(null)} />
          ) : (
            <div style={{ ...detailWrap, color: "#9aa4b2", fontStyle: "italic" }}>
              Click a check to see its full history and remediation guidance.
            </div>
          )}
        </div>
      </div>

      <div style={complianceFooter}>
        Watchdog runs daily at 06:00 UTC via `.github/workflows/compliance_watchdog.yml`. Findings persist in `compliance_check_log` (append-only) indefinitely for auditor traceability under HIPAA §164.308(a)(1)(ii)(D).
      </div>
    </div>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────

function SummaryCard({ label, value, sev }) {
  const s = SEVERITY_STYLE[sev] || SEVERITY_STYLE.info;
  return (
    <div style={{ background: "#fff", border: "1px solid #e1e6ed", borderRadius: 4, padding: 14 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", color: "#5a6478", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4, color: value > 0 ? s.color : "#5a6478" }}>{value}</div>
    </div>
  );
}

function FilterChip({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "4px 10px", fontSize: 12, borderRadius: 12,
        background: active ? "#1f6feb" : "#fff",
        color: active ? "#fff" : "#5a6478",
        border: `1px solid ${active ? "#1f6feb" : "#c9d6e8"}`,
        cursor: "pointer",
      }}
    >{label}</button>
  );
}

function CheckRow({ finding, active, onClick }) {
  const s = SEVERITY_STYLE[finding.severity] || SEVERITY_STYLE.info;
  const I = s.I;
  return (
    <div
      onClick={onClick}
      style={{
        padding: "10px 12px", cursor: "pointer", borderBottom: "1px solid #f0f3f7",
        background: active ? "#f4f7fb" : "transparent",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <I size={14} color={s.color} />
        <span style={{
          fontSize: 10, fontWeight: 700, textTransform: "uppercase",
          color: "#fff", background: s.color, padding: "2px 6px", borderRadius: 3,
        }}>{s.label}</span>
        <strong style={{ fontSize: 13 }}>{finding.check_id}</strong>
        <span style={{ fontSize: 11, color: "#5a6478", marginLeft: "auto" }}>
          {CATEGORY_LABEL[finding.category] || finding.category}
        </span>
      </div>
      <div style={{ marginTop: 4, fontSize: 12, color: "#1f2a3b" }}>
        {finding.message}
      </div>
      {finding.created_at && (
        <div style={{ marginTop: 3, fontSize: 10, color: "#9aa4b2" }}>
          Last checked: {new Date(finding.created_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}

function CheckDetail({ data, onClose }) {
  if (data.loading) return <div style={detailWrap}><Loader2 className="spin" /> Loading…</div>;
  if (data.error) return <div style={detailWrap}>Error: {data.error}</div>;
  if (!data.finding) return null;
  const f = data.finding;
  const s = SEVERITY_STYLE[f.severity] || SEVERITY_STYLE.info;

  return (
    <div style={detailWrap}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <h3 style={{ fontSize: 14, margin: 0 }}>{f.check_id}</h3>
        <button onClick={onClose} style={btnGhost}>Back</button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
        <span style={{
          fontSize: 10, fontWeight: 700, textTransform: "uppercase",
          color: "#fff", background: s.color, padding: "3px 8px", borderRadius: 3,
        }}>{s.label}</span>
        <span style={{ fontSize: 11, color: "#5a6478" }}>{CATEGORY_LABEL[f.category] || f.category}</span>
        {f.duration_ms != null && (
          <span style={{ fontSize: 10, color: "#9aa4b2", marginLeft: "auto" }}>
            {f.duration_ms}ms · run {f.run_id ? f.run_id.slice(0, 18) : ""}
          </span>
        )}
      </div>

      <div style={{ fontSize: 13, marginBottom: 10 }}>{f.message}</div>

      {f.remediation && (
        <div style={{ background: "#f4f7fb", border: "1px solid #c9d6e8", padding: 10, borderRadius: 3, marginBottom: 10 }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", color: "#5a6478", letterSpacing: 0.4, marginBottom: 4 }}>
            Remediation
          </div>
          <div style={{ fontSize: 12, color: "#1f2a3b", whiteSpace: "pre-wrap" }}>{f.remediation}</div>
        </div>
      )}

      {f.evidence && Object.keys(f.evidence).length > 0 && (
        <details style={{ marginBottom: 10 }}>
          <summary style={{ fontSize: 12, color: "#5a6478", cursor: "pointer" }}>Evidence</summary>
          <pre style={{ background: "#f9fafc", padding: 8, fontSize: 11, overflowX: "auto", borderRadius: 3, marginTop: 6 }}>
            {JSON.stringify(f.evidence, null, 2)}
          </pre>
        </details>
      )}

      {data.history && data.history.length > 1 && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ fontSize: 12, color: "#5a6478", cursor: "pointer" }}>
            History ({data.history.length} entries)
          </summary>
          <div style={{ marginTop: 6 }}>
            {data.history.map((h) => (
              <div key={h.id} style={{
                fontSize: 11, color: "#5a6478", borderTop: "1px solid #f0f3f7",
                padding: "4px 0", display: "flex", gap: 8,
              }}>
                <span style={{
                  fontSize: 9, fontWeight: 700, color: "#fff",
                  background: (SEVERITY_STYLE[h.severity] || SEVERITY_STYLE.info).color,
                  padding: "1px 5px", borderRadius: 2,
                }}>{h.severity.toUpperCase()}</span>
                <span>{new Date(h.created_at).toLocaleString()}</span>
                <span style={{ color: "#9aa4b2" }}>{h.message.slice(0, 80)}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

const detailWrap = {
  border: "1px solid #e1e6ed", borderRadius: 4, padding: 14, background: "#fff",
};
const btnGhost = {
  padding: "5px 10px", fontSize: 11, fontWeight: 600,
  background: "#fff", color: "#3a4a5e",
  border: "1px solid #c9d6e8", borderRadius: 3, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 4,
};
function alertStyle(kind) {
  return {
    padding: "6px 10px", borderRadius: 3, fontSize: 12, marginBottom: 12,
    display: "flex", alignItems: "center", gap: 6,
    background: kind === "ok" ? "#e9f5e9" : "#fde8e8",
    border: kind === "ok" ? "1px solid #a5d5a5" : "1px solid #f5a5a5",
    color: kind === "ok" ? "#2d6b2d" : "#8b0000",
  };
}
const complianceFooter = {
  marginTop: 18, padding: "10px 12px", fontSize: 11, color: "#5a6478",
  background: "#f4f7fb", border: "1px solid #c9d6e8", borderRadius: 4,
};
